import { HeadContent, Scripts, createRootRoute } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { ThemeProvider } from '@/providers/theme-provider'
import { CookieBanner } from '@/components/shared/cookie-banner'
import { ReactQueryDevtoolsWrapper } from '@/components/devtools/react-query-devtools'
import { SITE_DESCRIPTION, SITE_NAME } from '@/shared/lib/seo'

import appCss from '../styles.css?url'

/**
 * Root only carries what is genuinely the same on every page: encoding,
 * viewport, icons and a fallback title/description for any route that has not
 * defined its own head.
 *
 * Per-page title, description, canonical, Open Graph, Twitter and JSON-LD all
 * come from `seo()` in each route. In particular the canonical MUST NOT live
 * here: TanStack concatenates `links` across matches without deduping by
 * `rel`, so a root canonical would render next to each page's own and Google
 * would discard both.
 */
export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'theme-color', content: '#0a0f0d' },
      { name: 'author', content: 'Darren Hinde' },
      { title: `${SITE_NAME} — 1:1 AI coding workflow sessions` },
      { name: 'description', content: SITE_DESCRIPTION },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'manifest', href: '/site.webmanifest' },
    ],
  }),

  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-GB" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider defaultTheme="dark">
          <div className="relative z-10">{children}</div>
          <CookieBanner />
        </ThemeProvider>

        {/*
          Devtools are dev-only. They used to render unconditionally, which
          shipped both panels to real visitors and — because the React Query
          wrapper returns null on the server and a subtree on the client —
          caused a hydration mismatch on every page, throwing away the
          server-rendered markup and re-rendering it all on the client.
        */}
        {import.meta.env.DEV && (
          <>
            <TanStackDevtools
              config={{
                position: 'bottom-right',
              }}
              plugins={[
                {
                  name: 'Tanstack Router',
                  render: <TanStackRouterDevtoolsPanel />,
                },
              ]}
            />
            <ReactQueryDevtoolsWrapper />
          </>
        )}
        <Scripts />
      </body>
    </html>
  )
}
